import React from 'react';
import PropTypes from 'prop-types';
import { get, uniq } from 'lodash';
import { css, cx } from '@bd/bdui-styles/emotion';
import AccordionToggle from '../../components/AccordionToggle';
import { colors } from '../../styles';

const styles = css`
  .AccordionToggle_content {
    background-color: #000000;
    text-align: center;
    line-height: 31px;
    margin-bottom: 35px;
    margin: 0;
    color: #FFFFFF;
    padding: 50px 0;
    @media (min-width: 992px) {
      padding: 100px 0;
    }
  }
  .SectionAbout_descriptionSection {
    @media (min-width: 768px) {
      margin-bottom: 32px;
    }
    @media (min-width: 992px) {
      max-width: 950px; /* in case there's no schools */
      margin: 0 auto;
    }
  }
  .SectionAbout_description {
    font-size: 15px;
    font-weight: 300;
    letter-spacing: 0.022353em;
    line-height: 1.941176;
    margin: 0;
    @media (min-width: 768px) {
      font-size: 16px;
    }
    @media (min-width: 992px) {
      font-size: 17px;
    }
  }
  .SectionAbout_description-noResults {
    font-size: 16px;
    @media (min-width: 768px) {
      font-size: 18px;
    }
    @media (min-width: 992px) {
      font-size: 19px;
    }
  }
  .SectionAbout_schoolsSection {
    @media (min-width: 992px) {
      margin-left: 70px;
      width: 31.578947%;
      min-width: 220px;
      flex-shrink: 0;
    }
    @media (min-width: 1250px) {
      margin-left: 11.22807%;
    }
  }
  .SectionAbout_descriptionHead,
  .SectionAbout_schoolsHead {

  }
  .SectionAbout_schoolsListPrimary {
    margin: 0;
  }
  .SectionAbout_schoolsListItemPrimary {
    &:not(:last-child) {
      margin-bottom: 22.5px;
      @media (min-width: 768px) {
        margin-bottom: 23.5px;
      }
      @media (min-width: 992px) {
        margin-bottom: 26px;
      }
    }
  }
  .SectionAbout_schoolDistrict {
    display: block;
    font-size: 19px;
    font-weight: 300;
    letter-spacing: 0.022273em;
    line-height: 1.1;
    margin-bottom: 0.909091em;
    @media (min-width: 768px) {
      font-size: 20px;
    }
    @media (min-width: 992px) {
      font-size: 22px;
    }
  }
  .SectionAbout_schoolsListSecondary {
    margin: 0;
    line-height: 1.1;
  }
  .SectionAbout_schoolsListItemSecondary {
    &:not(:last-child) {
      margin-bottom: 13px;
      @media (min-width: 768px) {
        margin-bottom: 14px;
      }
      @media (min-width: 992px) {
        margin-bottom: 15px;
      }
    }
  }
  .SectionAbout_schoolsListLink {
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0.0225em;
    color: ${colors.color5};
    @media (min-width: 768px) {
      font-size: 15px;
    }
    @media (min-width: 992px) {
      font-size: 16px;
    }
  }
  @media (max-width: 767.98px) {
    .SectionAbout_descriptionSection + .SectionAbout_schoolsSection {
      margin-top: 28px;
    }
    .SectionAbout_descriptionHead-noResults {
      display: none;
    }
  }
  @media (min-width: 768px) {
    .SectionAbout_schoolsListPrimary {
      column-count: 2;
      column-gap: 60px;
    }
    .SectionAbout_schoolsListItemPrimaryInner {
      display: inline-block;
    }
  }
  @media (min-width: 992px) {
    .SectionAbout_schoolsListPrimary {
      column-count: auto;
    }
    .SectionAbout_schoolsListItemPrimaryInner {
      display: block;
    }
    .SectionAbout_schoolsSection:only-child { /* if no description */
      width: 100%;
      margin-left: 0;
      .SectionAbout_schoolsListPrimary {
        column-count: 2;
        column-gap: 80px;
      }
      .SectionAbout_schoolsListItemPrimaryInner {
        display: inline-block;
      }
    }
  }
`;


function SectionAbout({ className, community, description }) {
  const schools = get(community, 'schools', []);
  const hasSchools = schools.length > 0;
  const schoolsGrouped = [];
  if (hasSchools) {
    uniq(schools.map(school => school.district)).forEach(district => {
      schoolsGrouped.push({
        district,
        schools: schools.filter(school => get(school, 'district') === district),
      });
    });
  }
  return (
    <div className={cx(styles, className)}>
      <AccordionToggle
        openOnId="about"
        button="About This Property"
        id="about"
      >
        {
          description &&
          <div className="container">
            <h2 className="HomeDetail_sectionHead SectionAbout_descriptionHead GoldText">Home Description</h2>
            <p className="SectionAbout_description">{description}</p>
          </div>
        }
        {
          hasSchools &&
          <div className="SectionAbout_schoolsSection">
            <h3 className="HomeDetail_sectionHead SectionAbout_schoolsHead GoldText">Schools</h3>
            <ul className="SectionAbout_schoolsListPrimary list-unstyled">
              {
                schoolsGrouped.map(group => (
                  <li className="SectionAbout_schoolsListItemPrimary" key={group.district}>
                    <div className="SectionAbout_schoolslistItemPrimaryInner">
                      <span className="SectionAbout_schoolDistrict">{group.district}</span>
                      <ul className="SectionAbout_schoolsListSecondary list-unstyled">
                        {
                          get(group, 'schools', []).map(school => (
                            <li className="SectionAbout_schoolsListItemSecondary" key={school.name}>
                              <a
                                className="SectionAbout_schoolsListLink"
                                href={school.overviewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >{school.name}</a>
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  </li>
                ))
              }
            </ul>
          </div>
        }
        {
          (!description && !hasSchools) &&
          <div className="SectionAbout_descriptionSection">
            <h2 className={[
              'HomeDetail_sectionHead',
              'GoldText',
              'SectionAbout_descriptionHead',
              'SectionAbout_descriptionHead-noResults',
            ].join(' ')}
            >
              About this Property
            </h2>
            <p className="SectionAbout_description SectionAbout_description-noResults">
              {'No information available for this property at this time.'}
            </p>
          </div>
        }
      </AccordionToggle>
    </div>
  );
}

SectionAbout.propTypes = {
  className: PropTypes.string,
  community: PropTypes.object,
  description: PropTypes.string,
};

SectionAbout.defaultProps = {
  className: undefined,
  community: undefined,
  description: undefined,
};

export default SectionAbout;



// WEBPACK FOOTER //
// ./src/pages/HomeDetail/SectionAbout.js